import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { EnvironmentService } from '@pinnakl/core/environment';
import { withDevtools } from '@pinnakl/shared/signal-store';
import {
  CurrentUserInfoParsed,
  CurrentUserInfoUser,
  OtpChannelType,
  UserAuthType,
  UserRoles,
} from '@pinnakl/shared/types';
import { getAppInstanceToken } from '@pinnakl/shared/util-helpers';
import { CurrentUserApiService } from './current-user-api.service';

export type CurrentUserUpdatePayload = Partial<{
  firstName: string;
  lastName: string;
  phone: string;
  timeZone: string;
  password: string;
  authType: UserAuthType;
  otpChannel: OtpChannelType | null;
  tokenReauthInterval: number | null;
}>;

interface CurrentUserStoreState {
  currentUserInfo: CurrentUserInfoParsed | null;
  currentUserInfoFetchedAt: number | null;
  currentUserInfoLoading: boolean;
  currentUserInfoLoaded: boolean;
  currentUserUpdateProcessing: boolean;
}

const DEFAULT_TOKEN_REAUTH_INTERVAL_HOURS = 8;
const HOUR_MS = 60 * 60 * 1000;

function isUserInfoStale(user: CurrentUserInfoUser, fetchedAt: number | null): boolean {
  if (fetchedAt === null) return false;
  const lifetimeMs = (user.tokenReauthInterval ?? DEFAULT_TOKEN_REAUTH_INTERVAL_HOURS) * HOUR_MS;
  return Date.now() > fetchedAt + lifetimeMs;
}

const initialState: CurrentUserStoreState = {
  currentUserInfo: null,
  currentUserInfoFetchedAt: null,
  currentUserInfoLoading: false,
  currentUserInfoLoaded: false,
  currentUserUpdateProcessing: false,
};

export const CurrentUserStore = signalStore(
  { providedIn: 'root', protectedState: false },
  withDevtools('currentUser'),
  withState(initialState),
  withComputed(store => ({
    currentUser: computed(() => store.currentUserInfo()?.user ?? null),
  })),
  withComputed(store => ({
    isCurrentUserClientAdmin: computed(() => store.currentUser()?.rolesMap[UserRoles.ClientAdmin] ?? false),
  })),
  withMethods((store, api = inject(CurrentUserApiService), environmentService = inject(EnvironmentService)) => {
    const legacyUserInfoStorageKey = `user-${getAppInstanceToken(environmentService.get('authConfig')?.configId, location?.host, environmentService.get('appName'))}`;
    let inFlight: Promise<CurrentUserInfoParsed> | null = null;

    const fetchCurrentUserInfo = async (accessToken?: string): Promise<CurrentUserInfoParsed> => {
      patchState(store, { currentUserInfoLoading: true });
      try {
        const userInfo = await api.getCurrentUserInfo(accessToken);
        patchState(store, {
          currentUserInfo: userInfo,
          currentUserInfoFetchedAt: Date.now(),
          currentUserInfoLoaded: true,
        });
        return userInfo;
      } catch (e) {
        patchState(store, { currentUserInfoLoaded: false });
        throw e;
      } finally {
        patchState(store, { currentUserInfoLoading: false });
      }
    };

    const runFetch = (accessToken?: string): Promise<CurrentUserInfoParsed> => {
      const promise = fetchCurrentUserInfo(accessToken).finally(() => {
        if (inFlight === promise) inFlight = null;
      });
      inFlight = promise;
      return promise;
    };

    return {
      getCurrentUserInfo: (forceFetch = false, accessToken?: string): Promise<CurrentUserInfoParsed> => {
        if (!forceFetch) {
          if (inFlight) return inFlight;
          const storedCurrentUserInfo = store.currentUserInfo();
          if (storedCurrentUserInfo && !isUserInfoStale(storedCurrentUserInfo.user, store.currentUserInfoFetchedAt())) {
            return Promise.resolve(storedCurrentUserInfo);
          }
        }
        return runFetch(accessToken);
      },
      updateUser: async (userUpdateData: CurrentUserUpdatePayload): Promise<CurrentUserInfoParsed> => {
        patchState(store, { currentUserUpdateProcessing: true });
        try {
          const userInfo: CurrentUserInfoParsed = { user: await api.updateUserInfo(userUpdateData) };
          patchState(store, { currentUserInfo: userInfo, currentUserInfoFetchedAt: Date.now() });
          return userInfo;
        } finally {
          patchState(store, { currentUserUpdateProcessing: false });
        }
      },
      changePassword: async (password: string): Promise<void> => {
        patchState(store, { currentUserUpdateProcessing: true });
        try {
          return await api.changePassword(password);
        } finally {
          patchState(store, { currentUserUpdateProcessing: false });
        }
      },
      removeUser: (): void => {
        inFlight = null;
        localStorage.removeItem(legacyUserInfoStorageKey);
        patchState(store, initialState);
      },
    };
  }),
);
